import React, { forwardRef, Fragment, useImperativeHandle, useMemo, useCallback, useRef } from 'react';
import { useSearchPdfToolBar } from '../hooks/useSearchPdfToolBar';
import { PLACEHOLDER_TEXT } from '../dataFlow/index';
import { ISearchPdfToolBarRef, ISearchPdfToolBarProps } from '../interface';
import { isMobile } from '@umu/umu-ua';
import { LoadingSpinner } from './LoadingSpinner';
import { SearchCount } from './SearchCount';
import { NavigationButton } from './NavigationButton';
import { CloseButton } from './CloseButton';

const SearchPdfToolBar = forwardRef<ISearchPdfToolBarRef, ISearchPdfToolBarProps>((props, ref) => {
    const { className, placeholder = PLACEHOLDER_TEXT, ...rest } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const {
        value,
        isLoading,
        currentIndex,
        total,
        reset,
        close,
        setSelectedElement,
        getCurrentInfo,
        onChangeValue,
        onBeforeInput,
        onCompositionStart,
        onCompositionEnd,
        handleKeyDown,
        onNext,
        onPrevious,
    } = useSearchPdfToolBar(rest);
    const onInputFocus = useCallback(() => {
        inputRef.current?.focus();
    }, []);
    const onInputBlur = useCallback(() => {
        requestAnimationFrame(() => {
            inputRef.current?.blur();
        });
    }, []);
    // 暴露给父组件的方法
    useImperativeHandle(
        ref,
        () => ({
            reset,
            close,
            setSelectedElement,
            getCurrentInfo,
            onFocus: onInputFocus,
            onBlur: onInputBlur,
        }),
        [reset, close, setSelectedElement, getCurrentInfo, onInputBlur],
    );

    // 是否有搜索结果
    const hasResults = useMemo(() => total > 0, [total]);

    // 关闭处理
    const handleClose = useCallback(() => {
        close(true);
    }, [close]);

    // 当前选中索引（从1开始，用于显示）
    const displayIndex = useMemo(() => currentIndex + 1, [currentIndex]);

    // 右侧内容渲染
    // 始终渲染容器，使用 visibility 控制显示，避免宽度抖动
    // 即使没有内容也保持固定宽度，防止首次输入时宽度增大
    const rightContent = useMemo(() => {
        const currentCount = hasResults ? displayIndex : 0;
        const totalCount = hasResults ? total : 0;
        if (!value.trim() && isMobile) {
            return null;
        }
        return (
            <div
                className="search-pdf-toolbar-right-content"
                onClick={(e) => {
                    e.stopPropagation();
                    onInputFocus();
                }}
            >
                <div className="search-pdf-toolbar-right-item" style={{ visibility: isLoading ? 'visible' : 'hidden' }}>
                    <LoadingSpinner />
                </div>
                <div
                    className="search-pdf-toolbar-right-item"
                    style={{ visibility: !isLoading && (hasResults || value.trim()) ? 'visible' : 'hidden' }}
                >
                    <SearchCount current={currentCount} total={totalCount} />
                </div>
            </div>
        );
    }, [value, isLoading, hasResults, displayIndex, total, isMobile]);

    // 组合类名
    const containerClassName = useMemo(() => `search-pdf-toolbar ${className || ''}`.trim(), [className]);
    const renderInput = (showRightContent: boolean = true) => {
        return (
            <div className="search-pdf-toolbar-input-wrapper">
                <input
                    type="text"
                    className="search-pdf-toolbar-input"
                    value={value}
                    ref={inputRef}
                    autoFocus={!isMobile}
                    onBeforeInput={onBeforeInput}
                    onChange={onChangeValue}
                    onCompositionStart={onCompositionStart}
                    onCompositionEnd={onCompositionEnd}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    aria-label="search input"
                    autoComplete="off"
                />
                {showRightContent && <div className="search-pdf-toolbar-right">{rightContent}</div>}
            </div>
        );
    };
    const renderActions = () => {
        return (
            <Fragment>
                <NavigationButton direction="up" disabled={!hasResults} onClick={onPrevious} />
                <NavigationButton direction="down" disabled={!hasResults} onClick={onNext} />
            </Fragment>
        );
    };
    const renderContent = () => {
        if (isMobile) {
            return (
                <div className="search-pdf-toolbar-wrapper-mobile-content">
                    <div className="search-pdf-toolbar-wrapper-mobile">
                        <CloseButton onClose={handleClose} />
                        {renderInput(false)}
                    </div>
                    {value.trim() && (
                        <div className="search-pdf-toolbar-actions" role="group" aria-label="operation buttons">
                            {rightContent}
                            {renderActions()}
                        </div>
                    )}
                </div>
            );
        }
        return (
            <Fragment>
                {renderInput()}
                <div className="search-pdf-toolbar-actions" role="group" aria-label="operation buttons">
                    {renderActions()}
                    <div className="search-pdf-toolbar-divider" aria-hidden="true" />
                    <CloseButton onClose={handleClose} />
                </div>
            </Fragment>
        );
    };
    return (
        <div className={containerClassName} role="search" aria-label="PDF search toolbar">
            {renderContent()}
        </div>
    );
});
SearchPdfToolBar.displayName = 'SearchPdfToolBar';

export { SearchPdfToolBar };
